import { existsSync, promises as fs } from "fs";
import path from "path";
import { getConfig } from "@/src/utils/get-config";
import { handleError } from "@/src/utils/handle-error";
import { logger } from "@/src/utils/logger";
import chalk from "chalk";
import { Command } from "commander";
import { z } from "zod";
import prompts from "prompts";
import ora from "ora";
import {
  cleanDemoOnlyCode,
  extractDependencies,
  removeDuplicateFromArray,
} from "../utils/utils";
import { getPackageManager } from "../utils/get-package-manager";
import { execa } from "execa";

const addOptionsSchema = z.object({
  components: z.array(z.string()).optional(),
  yes: z.boolean(),
  overwrite: z.boolean(),
  cwd: z.string(),
  path: z.string().optional(),
});

const skippedDependencies = ["react", "react-dom"];

export const add = new Command()
  .name("add")
  .description("add a component to your project")
  .argument("[components...]", "the components to add")
  .option("-y, --yes", "skip confirmation prompt.", true)
  .option("-o, --overwrite", "overwrite existing files.", false)
  .option(
    "-c, --cwd <cwd>",
    "the working directory. defaults to the current directory.",
    process.cwd(),
  )
  .option("-p, --path <path>", "the path to add the component to.")
  .action(async (components, opts) => {
    try {
      const options = addOptionsSchema.parse({
        components,
        ...opts,
      });

      const cwd = path.resolve(options.cwd);

      if (!existsSync(cwd)) {
        logger.error(`The path ${cwd} does not exist. Please try again.`);
        process.exit(1);
      }

      const config = await getConfig(cwd);
      if (!config) {
        logger.warn(
          `Configuration is missing. Please run ${chalk.cyan(
            `init`,
          )} command to create a uilabs.config.json file.`,
        );
        process.exit(1);
      }

      if (!options.components?.length) {
        logger.warn("No components selected. Please provide a component name.");
        process.exit(0);
      }

      const componentsInfos = [];
      const dependencies = [];

      for (const component of options.components) {
        const componentInfoRes = await fetch(
          `https://uilabs.app/api/component/${component}`,
        );
        const componentInfo = await componentInfoRes.json();

        if (!componentInfo) {
          logger.warn(
            `Component ${chalk.cyan(component)} not found in the registry.`,
          );
          continue;
        }

        componentsInfos.push(componentInfo.data);
      }

      if (!options.yes) {
        const { proceed } = await prompts({
          type: "confirm",
          name: "proceed",
          message: `Ready to install components and dependencies. Proceed?`,
          initial: true,
        });

        if (!proceed) {
          process.exit(0);
        }
      }

      const spinner = ora(`Installing components...`).start();

      for (const componentInfo of componentsInfos) {
        const componentName =
          !componentInfo.name || componentInfo.name === "Untitled"
            ? componentInfo.installationId
            : componentInfo.name;

        spinner.text = `Installing ${chalk.cyan(componentName)}...`;

        const componentPath = path.join(
          config.resolvedPaths.components,
          "uilabs",
          componentInfo.installationId,
        );

        let code = componentInfo.code;
        let extensionFile = config.tsx ? ".tsx" : ".jsx";

        if (!config.tsx && componentInfo.language === "typescript") {
          spinner.stop();

          const { convertCode } = await prompts({
            type: "confirm",
            name: "convertCode",
            message: `The component ${chalk.cyan(
              componentName,
            )} is written in TypeScript and your project is configured to use JavaScript. Would you like to convert it to JavaScript?`,
            initial: true,
          });

          spinner.start();

          if (convertCode) {
            const jsxFetched = await fetch(
              `https://uilabs.app/api/converter/typescript-to-javascript`,
              {
                method: "POST",
                body: JSON.stringify({
                  code: componentInfo.code,
                }),
              },
            );

            if (jsxFetched.ok) {
              code = await jsxFetched.text();
            } else {
              const error = await jsxFetched.text();
              logger.error(
                `Failed to convert TypeScript to JavaScript: ${error}`,
              );
            }
          }
        }

        if (config.tsx && componentInfo.language === "javascript") {
          spinner.stop();

          const { cantConvertCode } = await prompts({
            type: "confirm",
            name: "cantConvertCode",
            message: `The component ${chalk.cyan(
              componentName,
            )} is written in JavaScript and your project is configured to use TypeScript. Would you like to install it as is?`,
            initial: true,
          });

          spinner.start();

          if (!cantConvertCode) {
            spinner.warn(`Skipped ${chalk.cyan(componentName)}.`);
            continue;
          }
        }

        // Verify if the file already exists
        if (existsSync(componentPath + extensionFile)) {
          if (options.overwrite) {
            spinner.text = `Overwriting ${chalk.cyan(componentName)}...`;
          } else {
            // Stop the spinner before prompting the user
            spinner.stop();

            const { overwrite } = await prompts({
              type: "confirm",
              name: "overwrite",
              message: `The file ${chalk.cyan(
                componentName,
              )} already exists. Do you want to overwrite it?`,
              initial: false,
            });

            // Restart the spinner after getting the user's response
            spinner.start();

            if (!overwrite) {
              spinner.warn(`Skipped ${chalk.cyan(componentName)}.`);
              continue;
            }
          }
        }

        dependencies.push(...extractDependencies(code));

        if (config.rsc) {
          code = `"use client"\n\n${code}`;
        }

        // Create the directory if it doesn't exist
        await fs.mkdir(path.dirname(componentPath), { recursive: true });
        // Write the component file
        await fs.writeFile(
          componentPath + extensionFile,
          cleanDemoOnlyCode(code),
          {
            flag: "w", // 'w' flag is used to overwrite the file
          },
        );

        try {
          await fetch(
            `https://uilabs.app/api/component/install/${componentInfo.installationId}`,
          );
        } catch (error) {}

        spinner.succeed(`Successfully installed ${chalk.cyan(componentName)}.`);
      }

      spinner.stop();

      const filteredDependencies = dependencies.filter(
        (dependency) => !skippedDependencies.includes(dependency),
      );
      const uniqueDependencies = removeDuplicateFromArray(filteredDependencies);

      if (!uniqueDependencies.length) {
        spinner.succeed(`Done.`);
        process.exit(0);
      }

      // Lire le package.json pour obtenir les dépendances installées
      const packageJsonPath = path.join(cwd, "package.json");
      const packageJson = JSON.parse(
        await fs.readFile(packageJsonPath, "utf-8"),
      );
      const installedDependencies = Object.keys(packageJson.dependencies || {});

      // Filtrer les dépendances qui ne sont pas encore installées
      const dependenciesToInstall = uniqueDependencies.filter(
        (dep) => !installedDependencies.includes(dep),
      );

      if (dependenciesToInstall.length > 0) {
        const spinnerDeps = ora(`Installing dependencies...`).start();

        const packageManager = await getPackageManager(cwd);

        await execa(
          packageManager,
          [
            packageManager === "npm" ? "install" : "add",
            ...dependenciesToInstall,
          ],
          {
            cwd,
          },
        );

        spinnerDeps.succeed(`Successfully installed dependencies.`);
      } else {
        console.log(`All dependencies are already installed.`);
      }
    } catch (error) {
      handleError(error);
    }
  });
